import {
  useQuery, useMutation, useQueryClient 
} from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getDiscrepanciesByPO,
  getSupplierDiscrepancies,
  proposeResolution,
  acceptResolution,
  disputeResolution,
  getDiscrepancyMessages,
  sendMessage,
  type ProposeResolutionPayload,
} from '../api/orders-client';
import { ValidationError } from '../errors';

const MOCK_SUPPLIER_ID = 'sup-sysco';

export function useDiscrepanciesByPO(poId: string | undefined) {
  return useQuery({
    queryKey: ['discrepancies', 'po', poId],
    queryFn: () => {
      if (!poId) {
        throw new ValidationError('poId', 'is required');
      }
      return getDiscrepanciesByPO(poId);
    },
    enabled: !!poId,
  });
}

export function useSupplierDiscrepancies() {
  return useQuery({
    queryKey: ['discrepancies', 'supplier', MOCK_SUPPLIER_ID],
    queryFn: () => getSupplierDiscrepancies(MOCK_SUPPLIER_ID),
  });
}

export function useProposeResolution() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      discrepancyId,
      payload,
    }: {
      discrepancyId: string;
      payload: ProposeResolutionPayload;
    }) => proposeResolution(discrepancyId, payload),
    onSuccess: () => {
      toast.success('Resolution proposal sent');
      queryClient.invalidateQueries({ queryKey: ['discrepancies'] });
    },
    onError: (error) => {
      toast.error(`Failed to propose resolution: ${error.message}`);
    },
  });
}

export function useAcceptResolution() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (discrepancyId: string) => acceptResolution(discrepancyId),
    onSuccess: () => {
      toast.success('Resolution accepted');
      queryClient.invalidateQueries({ queryKey: ['discrepancies'] });
    },
    onError: (error) => {
      toast.error(`Failed to accept resolution: ${error.message}`);
    },
  });
}

export function useDisputeResolution() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      discrepancyId,
      reason,
    }: {
      discrepancyId: string;
      reason: string;
    }) => disputeResolution(discrepancyId, reason),
    onSuccess: () => {
      toast.success('Dispute submitted');
      queryClient.invalidateQueries({ queryKey: ['discrepancies'] });
    },
    onError: (error) => {
      toast.error(`Failed to submit dispute: ${error.message}`);
    },
  });
}

export function useDiscrepancyMessages(discrepancyId: string | undefined) {
  return useQuery({
    queryKey: ['discrepancy-messages', discrepancyId],
    queryFn: () => {
      if (!discrepancyId) {
        throw new ValidationError('discrepancyId', 'is required');
      }
      return getDiscrepancyMessages(discrepancyId);
    },
    enabled: !!discrepancyId,
    refetchInterval: 30000,
  });
}

export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      discrepancyId,
      content,
    }: {
      discrepancyId: string;
      content: string;
    }) => sendMessage(discrepancyId, content),
    onSuccess: (_, { discrepancyId }) => {
      queryClient.invalidateQueries({ queryKey: ['discrepancy-messages', discrepancyId] });
    },
    onError: (error) => {
      toast.error(`Failed to send message: ${error.message}`);
    },
  });
}
